import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TournamentStatus } from '@repo/common-types';

export interface SngFiltersState {
  // Pagination
  skip: number;
  take: number;

  // Sorting
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';

  // Filters
  includeArchived?: boolean;
  filters?: {
    game?: string[];
    status?: TournamentStatus[];
    startTime?: { from?: Date; to?: Date };
    buyIn?: { min?: number; max?: number };
    name?: string;
  };
}

const initialState: SngFiltersState = {
  skip: 0,
  take: 10,
  sortBy: undefined,
  sortOrder: undefined,
  includeArchived: false,
  filters: {},
};

const sngFiltersSlice = createSlice({
  name: 'sngFilters',
  initialState,
  reducers: {
    setPagination: (state, action: PayloadAction<{ skip: number; take: number }>) => {
      state.skip = action.payload.skip;
      state.take = action.payload.take;
    },
    setSkip: (state, action: PayloadAction<number>) => {
      state.skip = action.payload;
    },
    setTake: (state, action: PayloadAction<number>) => {
      state.take = action.payload;
    },
    setSorting: (state, action: PayloadAction<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
      state.skip = 0; // Reset pagination when sorting changes
    },
    setIncludeArchived: (state, action: PayloadAction<boolean>) => {
      state.includeArchived = action.payload;
    },
    setFilters: (state, action: PayloadAction<SngFiltersState['filters']>) => {
      state.filters = action.payload;
      state.skip = 0; // Reset pagination when filters change
    },
    updateFilter: <K extends keyof NonNullable<SngFiltersState['filters']>>(
      state: SngFiltersState,
      action: PayloadAction<{ key: K; value: NonNullable<SngFiltersState['filters']>[K] }>,
    ) => {
      if (!state.filters) {
        state.filters = {};
      }
      state.filters[action.payload.key] = action.payload.value;
      state.skip = 0; // Reset pagination when filters change
    },
    clearSngSearchFilters: (state) => {
      state.filters = {};
      state.includeArchived = false;
      state.skip = 0; // Reset pagination when clearing filters
    },
    resetPagination: (state) => {
      state.skip = 0;
    },
    resetAll: () => initialState,
  },
});

export const {
  setPagination,
  setSkip,
  setTake,
  setSorting,
  setIncludeArchived,
  setFilters,
  updateFilter,
  clearSngSearchFilters,
  resetPagination,
  resetAll,
} = sngFiltersSlice.actions;

export default sngFiltersSlice.reducer;
